import React from 'react';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import MarkdownContent from '../MarkdownContent';
import { ToolComponentProps } from './types';

export const IntegrationGuideTool: React.FC<ToolComponentProps> = ({
  input,
  output,
  isExpanded,
  onToggle,
}) => {
  let parsedInput = input;
  if (typeof parsedInput === 'string') {
    try {
      parsedInput = JSON.parse(parsedInput);
    } catch {
      parsedInput = null;
    }
  }

  const integration = typeof parsedInput === 'object' && parsedInput !== null
    ? parsedInput.integration ?? parsedInput.Integration
    : undefined;
  const question = typeof parsedInput === 'object' && parsedInput !== null
    ? parsedInput.question ?? parsedInput.Question
    : undefined;
  const integrationName = typeof integration === 'string' && integration.length > 0 ? integration : 'Integration';
  const title = integrationName.charAt(0).toUpperCase() + integrationName.slice(1);

  let parsedOutput = output;
  if (typeof parsedOutput === 'string') {
    try {
      parsedOutput = JSON.parse(parsedOutput);
    } catch {
      // Keep as raw string if it isn't JSON
    }
  }

  const responseText = typeof parsedOutput === 'string'
    ? parsedOutput
    : typeof parsedOutput === 'object' && parsedOutput !== null
      ? parsedOutput.response ?? parsedOutput.answer ?? parsedOutput.content ?? ''
      : '';

  const hasResponse = responseText.trim().length > 0;

  return (
    <div className="space-y-3 border border-blue-200 bg-blue-50 rounded-lg p-3">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <span className="inline-flex w-2 h-2 bg-blue-500 rounded-full" aria-hidden />
          <span className="font-medium text-blue-800">
            {title} Integration Guide
          </span>
        </div>
        <div className="flex items-center gap-2 text-blue-600">
          {!isExpanded && hasResponse && (
            <span className="bg-blue-100 px-2 py-1 rounded">Expert Response</span>
          )}
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>

      {isExpanded && (
        <div className="space-y-3">
          <div className="bg-white border border-blue-200 rounded p-3">
            <div className="font-medium text-blue-700 mb-1">Question:</div>
            <div className="text-gray-900 whitespace-pre-wrap">
              {question ?? 'Requesting integration guidance...'}
            </div>
          </div>

          {hasResponse ? (
            <div className="bg-white border border-blue-200 rounded p-3">
              <div className="font-medium text-blue-700 mb-2">
                {title} Expert Response:
              </div>
              <div className="max-h-96 overflow-y-auto">
                <MarkdownContent
                  content={responseText}
                  variant="agent"
                  className="prose-code:bg-gray-100 prose-pre:bg-gray-50 prose-pre:border prose-pre:border-gray-200"
                />
              </div>
            </div>
          ) : (
            <div className="bg-white border border-blue-200 rounded p-3">
              <div className="flex items-center gap-2 text-blue-700">
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating {title} guide...
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
